import { booleanAttribute, Component, computed, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import { GenericValue } from '../../../../../core/model/generic-value';
import { HisNavigationService } from '../../../../../core/service/base/his-navigation.service';
import { splitTrimmed } from '../../../../../core/utils/his-string.utils';
import { LocalMedia, documentMetadataObjGuidOf } from '../../model/local-media.model';

/**
 * Number of rows shown per table page while the user has not chosen another size,
 * matching the default of the other paginated HISinOne tables.
 */
const DEFAULT_PAGE_SIZE = 10;

/** Upper bound the user may enter as the number of rows per page. */
const MAX_ROWS_PER_PAGE = 300;

/**
 * Time the typing has to pause before the filter is applied to the table. Long enough to
 * not re-render on every keystroke, short enough to still feel immediate.
 */
const FILTER_DEBOUNCE_MS = 300;

/**
 * Lets the user pick one of the files that are already available for the current
 * person ("local media") instead of uploading a new one.
 *
 * This is the Angular counterpart of the {@code localMedia} tab of the JSF
 * {@code <his:selectFile />} composite component. Like there, picking a file
 * applies it immediately — there is no separate "apply" action. Unlike there, the
 * filter is applied while typing instead of by a submit button.
 */
@Component( {
    standalone: false,
    selector: 'his-select-file-local-media',
    templateUrl: './his-select-file-local-media.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './his-select-file-local-media.component.css',
} )
export class HisSelectFileLocalMediaComponent {

    /** Separator of the filter terms, mirroring the JSF {@code COMMA_OR_BLANK_SEPARATED_SPLITTER}. */
    private readonly FILTER_SEPARATOR = /[\s,]+/;

    /** The local-media items offered for selection. */
    readonly media = input.required<LocalMedia[]>();

    /**
     * `true` once {@link media} has been loaded. Tells an empty selection apart from one that is
     * still on its way, which would otherwise show the "no files" hint while loading.
     */
    readonly mediaLoaded = input( false, { transform: booleanAttribute } );

    /** All document-metadata categories known to the server, used to resolve ids to labels. */
    readonly categories = input<GenericValue[]>( [] );

    readonly optionalLocalMediaResourceInfo = input<string[]>( ['description', 'category'] );

    protected readonly showDescription = computed( () => this.optionalLocalMediaResourceInfo().includes( 'description' ) );
    protected readonly showCategory = computed( () => this.optionalLocalMediaResourceInfo().includes( 'category' ) );

    /**
     * {@link LocalMedia.mediaReference} values that are already part of the current selection.
     * Those entries cannot be picked again: the selection is keyed by that reference, so a
     * second entry for the same file would be indistinguishable from the first one.
     */
    readonly selectedMediaReferences = input<string[]>( [] );

    /** Show the selection actions in a disabled state. */
    readonly disabled = input( false, { transform: booleanAttribute } );

    /** Emitted with the picked item when the user selects a local-media file. */
    readonly selectionChange = output<LocalMedia>();

    /** Emitted when the user cancels the selection. */
    readonly cancelSelection = output<void>();

    private readonly qisserverUrl = inject( HisNavigationService ).getQisserverUrl();

    /**
     * The filter text the user is currently typing. It narrows down the table once the
     * typing has paused for {@link FILTER_DEBOUNCE_MS}, or right away on {@code Enter}.
     */
    protected readonly filterDraft = signal( '' );

    /** The filter text currently applied to the table. */
    private readonly appliedFilter = signal( '' );

    /** Carries every keystroke in the filter field, debounced into {@link appliedFilter}. */
    private readonly filterInput = new Subject<string>();

    /** Index of the first row of the currently shown table page. */
    protected readonly first = signal( 0 );

    /** Number of rows shown per table page. */
    protected readonly rowsPerPage = signal( DEFAULT_PAGE_SIZE );

    /** Fallback and maximum of the rows-per-page input in the paginator. */
    protected readonly defaultPageSize = DEFAULT_PAGE_SIZE;
    protected readonly maxRowsPerPage = MAX_ROWS_PER_PAGE;

    /** The document-metadata categories by id, used to resolve the category column. */
    private readonly categoriesById = computed<Map<number, GenericValue>>( () =>
        new Map( this.categories().map( ( category ) => [category.id, category] ) )
    );

    /** The media references that are already selected, as a set for quick lookup. */
    private readonly selectedReferenceSet = computed<Set<string>>( () => new Set( this.selectedMediaReferences() ) );

    /**
     * The local-media items narrowed down by {@link appliedFilter}, mirroring the JSF
     * {@code filterLocalMediaResources()}: the filter is split into terms and an item is
     * kept when <em>every</em> term is contained — ignoring case — in its name or its
     * description.
     */
    protected readonly filteredMedia = computed<LocalMedia[]>( () => {
        const terms = splitTrimmed( this.appliedFilter(), this.FILTER_SEPARATOR )
            .filter( Boolean )
            .map( ( term ) => term.toLowerCase() );
        if ( terms.length === 0 ) {
            return this.media();
        }

        return this.media().filter( ( item ) => terms.every( ( term ) =>
            ( item.name?.toLowerCase().includes( term ) ?? false )
            || ( item.description?.toLowerCase().includes( term ) ?? false )
        ) );
    } );

    /** Number of the table page currently shown, counted from 1 for the paginator info. */
    protected readonly currentPageNumber = computed<number>( () =>
        Math.floor( this.first() / this.rowsPerPage() ) + 1
    );

    /** Number of table pages the filtered items are spread over. */
    protected readonly totalNumberOfPages = computed<number>( () =>
        Math.ceil( this.filteredMedia().length / this.rowsPerPage() )
    );

    constructor() {
        this.filterInput
            .pipe( debounceTime( FILTER_DEBOUNCE_MS ), takeUntilDestroyed() )
            .subscribe( ( filter ) => this.applyFilterText( filter ) );
    }

    /** Applies the drafted filter to the table, starting again on its first page. */
    protected applyFilter(): void {
        this.applyFilterText( this.filterDraft() );
    }

    /** Takes up a keystroke in the filter field; the table follows once the typing pauses. */
    protected onFilterInput( filter: string ): void {
        this.filterDraft.set( filter );
        this.filterInput.next( filter );
    }

    /** Clears both the drafted and the applied filter. */
    public resetFilter(): void {
        this.filterDraft.set( '' );
        this.filterInput.next( '' );
        this.applyFilterText( '' );
    }

    /** Narrows the table down to the given text, returning to its first page. */
    private applyFilterText( filter: string ): void {
        if ( filter === this.appliedFilter() ) {
            return;
        }

        this.appliedFilter.set( filter );
        this.first.set( 0 );
    }

    /** Keeps track of the table page the user paged to. */
    protected onPage( event: { first?: number } ): void {
        this.first.set( event.first ?? 0 );
    }

    /** Applies a new page size and returns to the first page. */
    protected onRowsPerPageChange( rows: number ): void {
        this.rowsPerPage.set( rows );
        this.first.set( 0 );
    }

    /** The label of the document-metadata category of the given item, or an empty string. */
    protected categoryLabel( item: LocalMedia ): string {
        return this.categoriesById().get( item.documentMetadataCategoryId )?.defaulttext ?? '';
    }

    /** `true` when the given item is already part of the current selection. */
    protected isSelected( item: LocalMedia ): boolean {
        return this.selectedReferenceSet().has( item.mediaReference );
    }

    /**
     * The download URL of the given item, mirroring the JSF trusted-domain download link;
     * {@code null} for media that is not backed by document metadata (e.g. files provided by
     * the server file system), which is therefore not downloadable.
     */
    protected downloadUrl( item: LocalMedia ): string | null {
        const documentMetadataObjGuid = documentMetadataObjGuidOf( item.mediaReference );
        if ( documentMetadataObjGuid == null ) {
            return null;
        }

        return `${this.qisserverUrl}/rds?state=docdownload&docId=${encodeURIComponent( documentMetadataObjGuid )}`;
    }
}
